import useProductsStore from '../../states/products';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ServiceItem from '../../Components/Book/ServiceItem/ServiceItem';
import ServiceSummary from '../../Components/Book/ServiceSummary/ServiceSummary';
import BookSummary from '../../Components/Book/BookSummary/BookSummary';
import Page from '../../Components/Page/BookingPage';
import BuildService from '../../Components/Book/BuildService/BuildService';
import {
    SKIPPED,
    buildIncompatibilityMap,
    findSelectedValue,
    getProductAttributes,
    getSelectedServiceDetails,
    isAnswered,
    isAttributeBlocked,
} from '../../utils/serviceBuilder';
import styles from './book.module.css';

export { SKIPPED, getProductAttributes, getSelectedServiceDetails };

function Book() {
    const navigate = useNavigate();
    const products = useProductsStore((state) => state.products);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedServices, setSelectedServices] = useState([]);
    const [selections, setSelections] = useState({});
    const [editingService, setEditingService] = useState(null);

    const attributes = useMemo(() => getProductAttributes(selectedProduct), [selectedProduct]);

    const draftService = useMemo(
        () => getSelectedServiceDetails(selectedProduct, selections),
        [selectedProduct, selections]
    );

    const blockedByAttribute = useMemo(
        () => buildIncompatibilityMap(attributes, selections),
        [attributes, selections]
    );

    const canSave = useMemo(
        () => attributes.every((attribute) => {
            const blockedValues = blockedByAttribute[attribute.name];
            const selected = findSelectedValue(attribute, selections);
            if (selected && blockedValues.has(selected.id)) return false;
            // Required only applies while at least one option can still be combined with the selection.
            if (isAttributeBlocked(attribute, blockedValues)) return true;
            return isAnswered(attribute, selections)
                && (!attribute.required || Boolean(selected));
        }),
        [attributes, selections, blockedByAttribute]
    );

    const findAddedService = (productID) => selectedServices.find(
        (service) => (service.product_id ?? service.product?.id) === productID
    );

    const handleProductClick = (product) => {
        const existingService = findAddedService(product.id);

        if (existingService) {
            handleEditService(existingService);
            return;
        }

        setSelectedProduct(product);
        setSelections({});
        setEditingService(null);
    };

    const handleSelectValue = (attributeIndex, valueID) => {
        const attribute = attributes[attributeIndex];

        setSelections((prev) => {
            const isDeselect = Object.prototype.hasOwnProperty.call(prev, attribute.name)
                && prev[attribute.name] === valueID;

            // Later answers are cleared so upcoming incompatibility rules never leave a stale pick behind.
            const next = {};

            for (let index = 0; index < attributeIndex; index += 1) {
                const previousName = attributes[index].name;

                if (Object.prototype.hasOwnProperty.call(prev, previousName)) {
                    next[previousName] = prev[previousName];
                }
            }

            // Re-picking the current answer clears it, reopening this step and everything after it.
            if (!isDeselect) {
                next[attribute.name] = valueID;
            }

            return next;
        });
    };

    const handleBack = () => {
        setSelectedProduct(null);
        setSelections({});
        setEditingService(null);
    };

    const handleSaveProduct = () => {
        if (!draftService || !canSave) {
            return;
        }

        setSelectedServices((prev) => {
            const target = editingService
                ?? prev.find((service) => (service.product_id ?? service.product?.id) === draftService.product_id);

            if (!target) {
                return [...prev, draftService];
            }

            return prev.map((service) => service === target ? draftService : service);
        });
        handleBack();
    };

    const handleRemoveService = (service) => {
        setSelectedServices((prev) => prev.filter((s) => s !== service));

        if (service === editingService) {
            handleBack();
        }
    };

    const handleEditService = (service) => {
        const productForEdit = products.find((product) => product.id === (service.product_id ?? service.product?.id))
            ?? service.product;
        const savedAttributes = service.selected_attributes ?? service.attributes ?? [];
        const savedValueIDsByName = new Map(
            savedAttributes.map((attribute) => [attribute.attribute_name ?? attribute.name, attribute.attribute_value_id ?? null])
        );
        const restoredSelections = {};

        getProductAttributes(productForEdit).forEach((attribute) => {
            restoredSelections[attribute.name] = savedValueIDsByName.has(attribute.name)
                ? savedValueIDsByName.get(attribute.name)
                : SKIPPED;
        });

        setEditingService(service);
        setSelectedProduct(productForEdit);
        setSelections(restoredSelections);
    };

    const handleScheduleClick = () => {
        if (!selectedServices.length) {
            return;
        }

        const scheduledServices = selectedServices.map((service) => {
            const selectedAttributePayload = Array.isArray(service.selected_attributes)
                ? service.selected_attributes
                : (Array.isArray(service.attributes) ? service.attributes : []);

            return {
                ...service,
                selected_attributes: selectedAttributePayload,
                attributes: selectedAttributePayload,
            };
        });

        const primaryService = scheduledServices[0] ?? {};
        const productDuration = scheduledServices.reduce(
            (total, service) => ({
                duration: total.duration + (Number(service?.productDuration?.duration) || 0),
                price: total.price + (Number(service?.productDuration?.price) || 0),
            }),
            { duration: 0, price: 0 }
        );

        navigate('/schedule', {
            state: {
                selected_services: scheduledServices,
                product: primaryService.product,
                selected_attributes: primaryService.selected_attributes ?? [],
                attribute_selection: primaryService.attribute_selection ?? {},
                product_id: primaryService.product_id ?? primaryService.product?.id,
                product_name: primaryService.product_name ?? primaryService.product?.name,
                product_description: primaryService.product_description ?? primaryService.product?.description,
                attributes: primaryService.attributes ?? [],
                productDuration,
            },
        });
    };

    return (
        <Page
            title="Book with Astraea"
            subtitle="Select and customize your services"

        >
                {selectedProduct ? (
                    <>
                        <BuildService
                            product={selectedProduct}
                            attributes={attributes}
                            selections={selections}
                            blockedByAttribute={blockedByAttribute}
                            basePrice={draftService.base_price}
                            baseDuration={draftService.base_duration}
                            onSelectValue={handleSelectValue}
                            onBack={handleBack}
                        />
                        <ServiceSummary
                            productName={selectedProduct.name}
                            basePrice={draftService.base_price}
                            baseDuration={draftService.base_duration}
                            selectedAttributes={draftService.selected_attributes}
                            totalPrice={draftService.total_price}
                            totalDuration={draftService.total_duration}
                            canSave={canSave}
                            onSaveClick={handleSaveProduct}
                            saveButtonLabel={editingService ? 'Update Service' : 'Add to Appointment'}
                        />
                    </>

                ) : (
                    <>
                        <div className={styles.services}>
                            {products.map((product) => (
                                <ServiceItem
                                    key={product.id}
                                    service={product}
                                    onClick={() => handleProductClick(product)}
                                />
                            ))}
                        </div>
                        <BookSummary 
                            selectedServices={selectedServices}
                            onScheduleClick={handleScheduleClick}
                            onRemoveService={handleRemoveService}
                            onEditService={handleEditService}
                        />
                    </>
                )}

        </Page>
    );
}

export default Book;